import * as api from "../api/report.filters.api";
import { reduceUsers } from "../utils/utilities";
import { User } from "../types/users";
import { Group } from "../types/groups";
import { Organisation } from "../types/organisation";
import { Listing, ListingShort } from "../types/listings";
import { HealthService } from "../types/healthservice";
import { FiltersDataLayer } from "../types/filters";
import { Hospital } from "../types/hospitals";

const state = {
	healthservices: [],
	groups: [],
	hospitals: [],
	listings: [],
	users: [],
	organisations: [],
	organisationListings: [],
	organisationUsers: [],
	fetching: false,
	dateOptions: [
		{ label: "Last 7 days", value: 7 },
		{ label: "Last 14 days", value: 14 },
		{ label: "Last 30 days", value: 30 },
		{ label: "Last 60 days", value: 60 },
		{ label: "Last 90 days", value: 90 },
		{ label: "Last 180 days", value: 180 },
		{ label: "Last 12 months", value: 365 }
	]
};

const getters = {
	categories: (state, getters) => {
		const groups = getters.groups;
		const categories = [
			{ label: "Acute", value: [], count: 0 },
			{ label: "ACAS", value: [], count: 0 },
			{ label: "Acute", value: [], count: 0 },
			{ label: "Emergency Department", value: [], count: 0 },
			{ label: "Geriatric Evaluation and Management", value: [], count: 0 },
			{ label: "In-community", value: [], count: 0 },
			{ label: "Sub Acute", value: [], count: 0 },
			{ label: "Transition Care Program", value: [], count: 0 },
			{ label: "Other", value: [], count: 0 }
		];

		groups.forEach((group) => {
			const _category = categories.find((cat) => cat.label === group.category);
			_category.value.push(group.id);
			if (group.users) {
				_category.count += group.users.length;
			}
		});
		return categories;
	},
	healthservices: (state) => state.healthservices,
	hospitals: (state, getters, rootState, rootGetters): Hospital[] => {
		let hospitals = state.hospitals;

		const healthserviceids = rootGetters["reportQueries/healthserviceIDs"];

		if (healthserviceids.length) {
			hospitals = hospitals.filter((hospital) =>
				healthserviceids.includes(hospital.healthserviceid)
			);
		}

		return hospitals as Hospital[];
	},
	groups: (state, getters, rootState, rootGetters): Group[] => {
		let groups = state.groups;
		const hospitalids = rootGetters["reportQueries/hospitalIDs"].length
			? rootGetters["reportQueries/hospitalIDs"]
			: getters.hospitals.map((h) => h.id);

		if (hospitalids.length) {
			groups = groups.filter((group) => hospitalids.includes(group.hospitalid));
		}

		return groups as Group[];
	},
	users: (state, getters, rootState, rootGetters): User[] => {
		let users = state.users;

		const groupids = rootGetters["reportQueries/groupIDs"].length
			? rootGetters["reportQueries/groupIDs"]
			: getters.groups.map((h) => h.id);

		if (groupids.length) {
			users = users.filter(
				(user) => user.group && groupids.includes(user.group.id)
			);
		}

		return users as User[];
	},
	organisations: (state): Organisation[] =>
		state.organisations as Organisation[],
	listings: (state, getters, rootState, rootGetters): ListingShort[] => {
		let listings = state.listings as ListingShort[];

		const orgids = rootGetters["reportQueries/organisations"];

		if (orgids.length) {
			listings = listings.filter((listing) =>
				orgids.includes(listing.organisationid)
			);
		}

		return listings as ListingShort[];
	},
	organisationListings: (state): Listing[] =>
		state.organisationListings as Listing[],
	organisationUsers: (state): User[] => state.organisationUsers as User[],
	fetching: (state): boolean => state.fetching as boolean,
	datalayer: (state, getters, rootState, rootGetters) => {
		const dateRange = rootGetters["reportQueries/dateRange"];

		const forDataLayer: FiltersDataLayer = {
			daterange: state.dateOptions.find((o) => o.value === dateRange).label
		};

		if (state.healthservices.length) {
			forDataLayer.healthservices = "All health services";
			const healthserviceids = rootGetters["reportQueries/healthserviceIDs"];
			if (healthserviceids.length) {
				forDataLayer.healthservices = state.healthservices
					.filter((item) => healthserviceids.includes(item.id))
					.map((item) => item.name)
					.join("|");
			}
		}

		if (state.hospitals.length) {
			forDataLayer.hospitals = "All hospitals";
			const hospitalids = rootGetters["reportQueries/hospitalIDs"];
			if (hospitalids.length) {
				forDataLayer.hospitals = state.hospitals
					.filter((item) => hospitalids.includes(item.id))
					.map((item) => item.name)
					.join("|");
			}
		}

		if (state.groups.length) {
			forDataLayer.groups = "All groups";
			const groupids = rootGetters["reportQueries/groupIDs"];
			if (groupids.length) {
				forDataLayer.groups = state.groups
					.filter((item) => groupids.includes(item.id))
					.map((item) => item.name)
					.join("|");
			}
		}

		if (state.users.length) {
			forDataLayer.users = "All users";
			const userids = rootGetters["reportQueries/users"];
			if (userids.length) {
				// We need to reduce the users array as there multiple users across multiple groups
				const reducedUsers = reduceUsers(state.users);
				forDataLayer.users = reducedUsers
					.filter((item) => userids.includes(item.id))
					.map((item) => `${item.firstname} ${item.lastname}`)
					.join("|");
			}
		}

		return { filters: forDataLayer };
	},
	dateOptions: (state) => state.dateOptions
};

const actions = {
	async fetchHealthServices({ commit }) {
		commit("LOAD_START");

		const healthservices = await api.fetchHealthServices();

		commit("UPDATE_HEALTH_SERVICES", healthservices);

		commit("LOAD_FINISH");
	},
	async fetchHospitals({ commit }) {
		commit("LOAD_START");

		const hospitals = await api.fetchHospitals();

		commit("UPDATE_HOSPITALS", hospitals);

		commit("LOAD_FINISH");
	},
	async fetchHospitalUsers({ commit }, id: number) {
		commit("LOAD_START");

		const users = await api.fetchHospitalUsers(id);

		commit("UPDATE_USERS", users);

		commit("LOAD_FINISH");
	},
	async fetchHospitalGroups({ commit }, id: number) {
		commit("LOAD_START");

		const groups = await api.fetchHospitalGroups(id);

		commit("UPDATE_GROUPS", groups);

		commit("LOAD_FINISH");
	},
	async fetchUserHospitalGroups({ commit }, id: number) {
		commit("LOAD_START");
		const response = await api.getUserHospitalGroups(id);

		if (response) {
			const hospitals = [];
			const groups = [];
			const users = [];

			response.forEach((hospital) => {
				const { id, name, healthserviceid } = hospital;
				hospitals.push({ id, name, healthserviceid });

				if (hospital.groups) {
					hospital.groups.forEach((group) => {
						// including the hospital name so easier to read and pick in groups with multiple hospitals
						if (response.length > 1) {
							group.name = `${hospital.name} - ${group.name}`;
						}
						groups.push(group);
						if (group.users) {
							group.users.forEach((user) => {
								user.hospital = hospital;
								user.group = group;
								users.push(user);
							});
						}
					});
				}
			});

			commit("UPDATE_HOSPITALS", hospitals);
			commit("UPDATE_GROUPS", groups);
			commit("UPDATE_USERS", users);
		}
		commit("LOAD_FINISH");
	},
	async fetchOrganisations({ commit }) {
		commit("LOAD_START");

		const organisations = await api.fetchOrganisations();

		commit("UPDATE_ORGANISATIONS", organisations);

		commit("LOAD_FINISH");
	},
	async fetchListings({ commit }) {
		commit("LOAD_START");

		const _listings = await api.fetchListings();

		commit("UPDATE_LISTINGS", _listings);

		commit("LOAD_FINISH");
	},
	async fetchOrganisationListings({ commit }, id) {
		commit("LOAD_START");

		const listings = await api.fetchOrgListings(id);

		commit("UPDATE_ORGANISATION_LISTINGS", listings);

		commit("LOAD_FINISH");
	},
	async fetchOrganisationUsers({ commit }, id) {
		commit("LOAD_START");

		const users = await api.fetchOrgUsers(id);

		commit("UPDATE_ORGANISATION_USERS", users);

		commit("LOAD_FINISH");
	},
	addDateOption({ commit }, option) {
		commit("UPDATE_DATE_OPTIONS", option);
	}
};

const mutations = {
	UPDATE_HEALTH_SERVICES(state, healthservices: HealthService[]) {
		state.healthservices = healthservices;
	},
	UPDATE_GROUPS(state, groups: Group[]) {
		state.groups = groups;
	},
	UPDATE_HOSPITALS(state, hospitals) {
		state.hospitals = hospitals;
	},
	UPDATE_USERS(state, users: User[]) {
		state.users = users;
	},
	UPDATE_ORGANISATIONS(state, organisations: Organisation[]) {
		state.organisations = organisations;
	},
	UPDATE_LISTINGS(state, listings: Listing[]) {
		state.listings = listings;
	},
	UPDATE_ORGANISATION_LISTINGS(state, listings: Listing[]) {
		state.organisationListings = listings;
	},
	UPDATE_ORGANISATION_USERS(state, users: User[]) {
		state.organisationUsers = users;
	},
	UPDATE_DATE_OPTIONS(state, option) {
		state.dateOptions.push(option);
	},
	LOAD_START(state) {
		state.fetching = true;
	},
	LOAD_FINISH(state) {
		state.fetching = false;
	}
};

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters
}; // eslint-disable-line
